import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  deleteCachbackItem,
  getCashbacksList,
  handlePaginate,
  handleSelectAll,
  handleSelectItem,
  updateCashbacksBunch,
} from "store/liveOps/casbacks/slice";
import { cashbacksListLimits } from "configs/limits";
import { ModalTypes } from "configs/types";
import { openModal } from "store/modals/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";

import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import Pagination from "components/Pagination";
import ActionBtn from "components/buttons/ActionBtn";
import CashbacksFilter from "./CashbacksFilter";
import { Table } from "components/table/Table";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { RefreshButton } from "components/buttons/RefreshButton";
import { useRefreshList } from "helpers/useRefreshList";

const CashbacksList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const permissions = usePermissionHelper();
  const canRead = permissions?.read ?? false;
  const canCreate = permissions?.create ?? false;
  const canUpdate = permissions?.update ?? false;
  const canDelete = permissions?.delete ?? false;

  const [order, setOrder] = useState<{
    title: string;
    isActive: string;
    type: string;
    level: string;
    id: string;
  }>({
    title: "none",
    isActive: "none",
    type: "none",
    level: "none",
    id: "none",
  });
  const [currentLimit, setLimit] = useState<number>(cashbacksListLimits[0]);
  const [search, setSearch] = useState<string>("");
  const [level, setLevel] = useState<string>("");
  const [active, setActive] = useState<
    { value: number | string; label: string }[]
  >([{ value: 3, label: "All" }]);
  const [type, setType] = useState<{ value: string | number; label: string }[]>(
    [],
  );
  const options = [
    {
      label: t("activate"),
      value: "activate",
    },
    {
      label: t("hide"),
      value: "hide",
    },
  ];

  const list = useAppSelector((store) => store.cashbacks.list);
  const isListFetched = useAppSelector(
    (store) => store.cashbacks.isListFetched,
  );
  const totalItems = useAppSelector((store) => store.cashbacks.totalItems);
  const totalPages = useAppSelector((store) => store.cashbacks.totalPages);
  const activePage = useAppSelector((store) => store.cashbacks.activePage);
  const selectedItems = useAppSelector(
    (store) => store.cashbacks.selectedItems,
  );
  const processingItemId = useAppSelector(
    (store) => store.cashbacks.processingItemId,
  );
  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'cashbacks',
    isListFetched
  });
  const location = useLocation();

  useEffect(() => {
    if (!isListFetched) {
      dispatch(
        getCashbacksList({
          limit: currentLimit,
          offset: activePage - 1,
          title: search,
          isActive:
            active[0] && active[0].value !== 3
              ? (active[0].value as number)
              : undefined,
          level: level,
          types: type.map((el) => el.value) as string[],
          order,
        }),
      );
    }
  }, [isListFetched, activePage, currentLimit, order]);

  const handleAllClick = () => {
    if (!canUpdate) return;
    dispatch(handleSelectAll());
  };
  const handleItemClick = (id: number) => {
    if (!canUpdate) return;
    dispatch(handleSelectItem(id));
  };
  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };
  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };

  const handleActionClick = async (e: string) => {
    if (!canUpdate) return;
    try {
      await dispatch(
        updateCashbacksBunch({
          ids: selectedItems,
          isActive: e === "activate" ? 1 : 0,
        }),
      );
    } catch (error) {
      console.log(error, "error");
    }
  };

  const handleOpenEditModal = (id: number) => {
    if (!canRead) return;
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.cashback}&entityId=${id}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.cashback }));
  };
  const handleOpenCreateModal = () => {
    if (!canCreate) return;
    navigate(`${location.pathname}?activeModal=${ModalTypes.cashback}`);
    dispatch(openModal({ activeModal: ModalTypes.cashback }));
  };

  const handleOrderClick = (key: keyof typeof order) => {
    const current = order[key];
    let next: "asc" | "desc" | "none";

    if (current === "none") next = "asc";
    else if (current === "asc") next = "desc";
    else next = "none";

    const newOrder = {
      [key]: next,
      ...Object.fromEntries(Object.entries(order).filter(([k]) => k !== key)),
    };
    // @ts-ignore
    setOrder(newOrder);
    dispatch(handlePaginate(1));
  };

  const handleDeleteItem = async (id: number) => {
    if (!canDelete) return;
    const result = await dispatch(deleteCachbackItem(id ?? 0));
    if (result?.payload === true) {
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityDeleteSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } else {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("entityDeleteError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <CashbacksFilter
        handleApply={() => {
          dispatch(handlePaginate(1));
        }}
        searchValue={search}
        handleLevel={setLevel}
        levelValue={level}
        handleSearch={setSearch}
        selectedActive={active}
        handleUpdateActive={setActive}
        selectedType={type}
        handleUpdateType={setType}
      />
      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          <div className="page-list-header--box">
            {canCreate && (
              <ActionBtn
                txt={t("addCashback")}
                handleClick={handleOpenCreateModal}
              />
            )}
          </div>
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton
              position="right"
              disabled={isRefreshRequested}
              isLoading={isRefreshRequested}
              onClick={() => refresh()}
            />
          </div>
          <div className="page-list-header--box">
            <DefaultDropdown
              onSelect={(e) => {
                handleActionClick(e as string);
              }}
              placeholder={t("chooseAction")}
              options={options}
              isSmall
              disabled={selectedItems.length === 0 || canUpdate}
            />
          </div>
        </div>

        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={7} rowsCount={6} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && (
          <>
            <div className="game-table-wrap">
              {list.length === 0 && (
                <EmptyState message={t("noCashbackFound")} />
              )}

              {list.length > 0 && (
                <Table
                  data={list}
                  order={order}
                  columns={{
                    columnOrder: [
                      "id",
                      "image",
                      "title",
                      "level",
                      "type",
                      "isActive",
                      "claimExpirationHours",
                    ],
                    hiddenColumns: [
                      "loyaltyLevelId",
                      "checkingAt",
                      "content",
                      "bonusTemplateId",
                    ],
                    sortingColumns: {
                      claimExpirationHours: {
                        value: "Expiration (H)",
                      },
                    },
                  }}
                  idField="id"
                  selectedItems={selectedItems}
                  hasCheckbox={canUpdate}
                  hasEdit={canUpdate}
                  hasView={canRead && !canUpdate}
                  hasDelete={canDelete}
                  hasOptions={canRead || canUpdate || canDelete}
                  onAllSelect={handleAllClick}
                  onSelect={(id) => handleItemClick(id)}
                  onDelete={(id) => handleDeleteItem(id)}
                  onEdit={canUpdate ? (id) => handleOpenEditModal(id) : undefined}
                  onView={
                    canRead && !canUpdate
                      ? (id) => handleOpenEditModal(id)
                      : undefined
                  }
                  onSort={(id) => (id ? handleOrderClick(id) : {})}
                  processingId={processingItemId}
                />
              )}
            </div>

            {list.length > 0 && (
              <Pagination
                handleLimitClick={(e) => {
                  setLimit(e);
                  dispatch(handlePaginate(1));
                }}
                limitsArray={cashbacksListLimits}
                activePage={activePage}
                limit={currentLimit}
                totalItems={totalItems}
                totalPages={totalPages}
                handleNextClick={handleNextClick}
                handlePrevClick={handlePrevClick}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CashbacksList;
